import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, KeyRound, Plus, Radio, Trash2 } from 'lucide-react';
import { DataTable, Dialog, FilterBar } from '@/components/ui/layout';
import { get, post, put, session } from '@/lib/api';
import { getPages, type Paged } from './pagedList';
import { describeError, useAuth } from '@/lib/auth';
import { cn, dateTime, humanize, relative , copyText } from '@/lib/utils';
import { Alert, Badge, Button, Input, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { visibleInterval } from '@/lib/polling';
import { UnmatchedResultsDialog } from './UnmatchedResultsDialog';
import { batFileSlug, batSafe } from '@his/shared';

const BRIDGE_LISTEN_PORT = 5300;
const DEFAULT_GATEWAY_PORT = 5210;

export interface Instrument {
  id: string;
  code: string;
  name: string;
  manufacturer: string | null;
  protocol: 'hl7' | 'astm' | 'image-import';
  keyPrefix: string;
  manualMode: boolean;
  isActive: boolean;
  lastSeenAt: string | null;
  lastMessageAt: string | null;
  notes: string | null;
  connection?: 'bridge' | 'direct' | null;
  listenPort?: number | null;
  listening?: number | null;
  _count: { testMaps: number; messages: number };
}

interface TestMap {
  id: string;
  analyserCode: string;
  testId: string;
  test: { id: string; code: string; name: string; unit: string | null };
}

interface Message {
  id: string;
  format: string;
  accessionNo: string | null;
  status: string;
  accepted: number;
  rejected: { testCode: string; reason: string }[];
  error: string | null;
  raw: string;
  receivedAt: string;
}

const ONLINE_WINDOW_MS = 3 * 60_000;

/**
 * The analysers connected to this lab (SRS 8.1).
 *
 * A machine sends results through a small bridge program on the PC beside
 * it; the bridge identifies itself with the key issued here. What arrives
 * lands on the bench as an entered result — never released — and every
 * frame is kept so "the machine ran it, where is it?" has an answer.
 */
export function LabInstrumentsTab({ manage, tests }: { manage: boolean; tests: { id: string; code: string; name: string; unit: string | null }[] }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState<{ name: string; code: string; key: string } | null>(null);
  const [mapping, setMapping] = useState<Instrument | null>(null);
  const [viewing, setViewing] = useState<Instrument | null>(null);
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);
  const gateway = useQuery({
    queryKey: ['lab-gateway'],
    queryFn: () => get<{ enabled: boolean; host: string; port: number; ports?: number[]; connections: number; cloud?: boolean; pendingUnmatched: number }>('/lab/instruments/gateway'),
    refetchInterval: visibleInterval(60_000),
  });

  const instruments = useQuery({
    queryKey: ['lab-instruments'],
    queryFn: () => getPages<Instrument>('/lab/instruments', { limit: 100 }),
    refetchInterval: visibleInterval(60_000),
  });

  const toggle = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Instrument> }) => put(`/lab/instruments/${id}`, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['lab-instruments'] }),
    onError: (error) => toast.error(tr('lab', 'could_not_update_the_instrument'), describeError(error)),
  });

  const rotate = useMutation({
    mutationFn: (row: Instrument) => post<Instrument & { key: string }>(`/lab/instruments/${row.id}/rotate-key`, {}),
    onSuccess: (result) => {
      setRevealed({ name: result.name, code: result.code, key: result.key });
      void queryClient.invalidateQueries({ queryKey: ['lab-instruments'] });
    },
    onError: (error) => toast.error(tr('lab', 'could_not_rotate_the_key'), describeError(error)),
  });

  const online = (row: Instrument) => row.lastSeenAt && Date.now() - new Date(row.lastSeenAt).getTime() < ONLINE_WINDOW_MS;

  return (
    <>
      <FilterBar
        trailing={
          <Button variant={gateway.data && gateway.data.pendingUnmatched > 0 ? 'default' : 'outline'} onClick={() => setUnmatchedOpen(true)}>
            {tr('lab', 'unmatched_results')}{gateway.data && gateway.data.pendingUnmatched > 0 ? ` (${gateway.data.pendingUnmatched})` : ''}
          </Button>
        }
      >
        <p className="self-center text-sm text-muted-foreground">{tr('lab', 'each_analyser_gets_its_own_key_the_bridge')}</p>
      </FilterBar>
      {gateway.data && (
        <Alert className="mb-3">
          <span className="flex items-center gap-2">
            <Radio className="h-4 w-4" />
            {gateway.data.enabled
              ? tr('lab', 'analyser_gateway_listening_on_x_x_x_bridge_s', { hostname: window.location.hostname, value: (gateway.data.ports ?? [gateway.data.port]).join(', '), connections: gateway.data.connections })
              : gateway.data.cloud
                ? tr('lab', 'analysers_reach_this_cloud_server_only_through')
                : tr('lab', 'the_analyser_gateway_is_off_on_this_server')}
          </span>
        </Alert>
      )}

      <DataTable
        rows={instruments.data}
        loading={instruments.isLoading} refreshing={instruments.isFetching}
        error={instruments.error}
        rowKey={(row) => row.id}
        empty={tr('lab', 'no_analysers_connected_yet_add_one_to_get_a_key')}
        columns={[
          {
            header: tr('lab', 'instrument'),
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <p className={cn('font-medium', !row.isActive && 'text-muted-foreground line-through')}>{row.name}</p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-mono">{row.code}</span>
                  {row.manufacturer ? ` · ${row.manufacturer}` : ''} · {humanize(row.protocol, 'instrument_protocol')}
                </p>
              </div>
            ),
          },
          {
            header: tr('lab', 'key'),
            sort: (row) => row.keyPrefix,
            cell: (row) => <span className="font-mono text-xs text-muted-foreground">{row.keyPrefix}…</span>,
          },
          {
            header: tr('lab', 'last_seen'),
            sort: (row) => row.lastSeenAt,
            cell: (row) => (
              <div className="flex items-center gap-1.5 text-sm">
                <span className={cn('h-2 w-2 rounded-full', online(row) ? 'bg-success' : 'bg-muted-foreground/40')} />
                {row.lastSeenAt ? relative(row.lastSeenAt) : tr('lab', 'never')}
              </div>
            ),
          },
          {
            header: tr('lab', 'messages'),
            align: 'right',
            sort: (row) => row._count.messages,
            cell: (row) => (
              <button type="button" className="text-sm underline-offset-4 hover:underline" onClick={() => setViewing(row)}>
                {row._count.messages}
              </button>
            ),
          },
          {
            header: tr('lab', 'codes_mapped'),
            align: 'right',
            sort: (row) => row._count.testMaps,
            cell: (row) => (
              <button type="button" className="text-sm underline-offset-4 hover:underline" onClick={() => setMapping(row)}>
                {row._count.testMaps}
              </button>
            ),
          },
          {
            header: tr('lab', 'status'),
            sort: (row) => row.isActive,
            cell: (row) => (
              <div className="flex gap-1">
                {!row.isActive && <Badge variant="muted">{tr('lab', 'disabled')}</Badge>}
                {row.isActive && row.manualMode && <Badge variant="warning">{tr('lab', 'manual_mode')}</Badge>}
                {row.isActive && !row.manualMode && <Badge variant="success">{tr('lab', 'receiving')}</Badge>}
                {row.listening && <Badge variant="secondary">{tr('lab', 'direct')}{row.listening}</Badge>}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              manage ? (
                <div className="flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant={row.manualMode ? 'default' : 'outline'}
                    onClick={() => toggle.mutate({ id: row.id, patch: { manualMode: !row.manualMode } })}
                    title={tr('lab', 'while_on_frames_from_this_machine_are_refused')}
                  >
                    {row.manualMode ? tr('lab', 'resume') : tr('lab', 'manual')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => rotate.mutate(row)} title={tr('lab', 'issue_a_new_key')}>
                    <KeyRound className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/lab/instruments/${row.id}/edit`)}>
                    {tr('lab', 'edit')}
                  </Button>
                </div>
              ) : null,
          },
        ]}
      />

      {revealed && <KeyDialog revealed={revealed} onClose={() => setRevealed(null)} />}
      {mapping && <TestMapsDialog instrument={mapping} tests={tests} manage={manage} onClose={() => setMapping(null)} />}
      {viewing && <MessagesDialog instrument={viewing} onClose={() => setViewing(null)} />}
      {unmatchedOpen && <UnmatchedResultsDialog canResolve={manage || can('lab.result.enter')} onClose={() => { setUnmatchedOpen(false); void queryClient.invalidateQueries({ queryKey: ['lab-gateway'] }); }} />}
    </>
  );
}

export function KeyDialog({ revealed, onClose }: { revealed: { name: string; code: string; key: string }; onClose: () => void }) {
  const toast = useToast();
  const apiUrl = `${window.location.origin}`;
  const tenant = session.tenant() ?? '<subdomain>';
  const copy = async (text: string) => {
    if (await copyText(text)) {
      toast.success(tr('lab', 'copied'));
    } else {
      toast.error(tr('lab', 'could_not_copy_select_and_copy_by_hand'));
    }
  };
  const env = [
    `HIS_API_URL=${apiUrl}`,
    `HIS_TENANT=${tenant}`,
    `HIS_INSTRUMENT_KEY=${revealed.key}`,
    `LISTEN_PORT=${BRIDGE_LISTEN_PORT}`,
  ].join('\n');
  const gateway = useQuery({
    queryKey: ['lab-gateway'],
    queryFn: () => get<{ enabled: boolean; port: number }>('/lab/instruments/gateway'),
  });
  const gatewayHost = batSafe(window.location.hostname);
  const gatewayPort = batSafe(gateway.data?.port || DEFAULT_GATEWAY_PORT);
  const safeTenant = batSafe(tenant);
  const safeCode = batSafe(revealed.code);
  const safeKey = batSafe(revealed.key);
  const safeName = batSafe(revealed.name);
  const download = (name: string, text: string) => {
    const blob = new Blob([text.replace(/\n/g, '\r\n')], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
  };
  const serialBat = [
    '@echo off',
    'setlocal',
    'cd /d "%~dp0"',
    'set SERIAL_BRIDGE_COM=COM1',
    'set SERIAL_BRIDGE_BAUD=9600',
    'set SERIAL_BRIDGE_DATABITS=8',
    'set SERIAL_BRIDGE_PARITY=None',
    'set SERIAL_BRIDGE_STOPBITS=1',
    'set SERIAL_BRIDGE_HANDSHAKE=None',
    'set SERIAL_BRIDGE_DTR=true',
    'set SERIAL_BRIDGE_RTS=true',
    `set SERIAL_BRIDGE_HOST=${gatewayHost}`,
    `set SERIAL_BRIDGE_PORT=${gatewayPort}`,
    `set SERIAL_BRIDGE_LAB_CODE=${safeTenant}`,
    `set SERIAL_BRIDGE_DEVICE_NAME=${safeCode}`,
    `set SERIAL_BRIDGE_KEY=${safeKey}`,
    'set SERIAL_BRIDGE_HEALTH_PORT=9103',
    `echo Starting the serial bridge for ${safeName} ...`,
    '"%~dp0limspak-serial-bridge.exe"',
    'pause >nul',
  ].join('\n');
  const tcpBat = [
    '@echo off',
    'setlocal',
    'cd /d "%~dp0"',
    'set TCP_BRIDGE_LISTEN_HOST=0.0.0.0',
    `set TCP_BRIDGE_LISTEN_PORT=${BRIDGE_LISTEN_PORT}`,
    `set TCP_BRIDGE_HOST=${gatewayHost}`,
    `set TCP_BRIDGE_PORT=${gatewayPort}`,
    `set TCP_BRIDGE_LAB_CODE=${safeTenant}`,
    `set TCP_BRIDGE_DEVICE_NAME=${safeCode}`,
    `set TCP_BRIDGE_KEY=${safeKey}`,
    'set TCP_BRIDGE_HEALTH_PORT=9106',
    `echo Starting the TCP relay bridge for ${safeName} ...`,
    '"%~dp0limspak-tcp-bridge.exe"',
    'pause >nul',
  ].join('\n');

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title={tr('lab', 'key_for_x', { name: revealed.name })}
      description={tr('lab', 'shown_once_put_it_in_the_bridge_beside_the')}
      footer={<Button onClick={onClose}>{tr('lab', 'done_i_have_saved_it')}</Button>}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Input readOnly value={revealed.key} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
          <Button variant="outline" size="icon" aria-label={tr('lab', 'copy_key')} onClick={() => copy(revealed.key)}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
        <Alert>
          {tr('lab', 'serial_rs_232_or_network_analyser_download_the')}{' '}
          <span className="font-mono">{gatewayHost}:{gatewayPort}</span> {tr('lab', 'with_the_key_baked_in')}
        </Alert>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => download(`run-serial-${batFileSlug(revealed.code)}.bat`, serialBat)}>
            <Download className="me-1 h-4 w-4" /> {tr('lab', 'run_bat_serial_bridge_com_port')}
          </Button>
          <Button variant="outline" onClick={() => download(`run-tcp-${batFileSlug(revealed.code)}.bat`, tcpBat)}>
            <Download className="me-1 h-4 w-4" /> {tr('lab', 'run_bat_tcp_relay_bridge_lan_analyser')}
          </Button>
        </div>
        <Alert>
          {tr('lab', 'https_bridge_instead_run')} <span className="font-mono">tools/analyser-bridge</span> {tr('lab', 'with_this_configuration_and_point_the_analysers')}
        </Alert>
        <div className="relative">
          <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">{env}</pre>
          <Button variant="ghost" size="icon" className="absolute end-1 top-1" aria-label={tr('lab', 'copy_configuration')} onClick={() => copy(env)}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function TestMapsDialog({
  instrument,
  tests,
  manage,
  onClose,
}: {
  instrument: Instrument;
  tests: { id: string; code: string; name: string; unit: string | null }[];
  manage: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<{ analyserCode: string; testId: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const maps = useQuery({
    queryKey: ['lab-instrument-maps', instrument.id],
    queryFn: () => get<TestMap[]>(`/lab/instruments/${instrument.id}/test-maps`),
  });
  if (maps.data && rows === null) {
    setRows(maps.data.map((m) => ({ analyserCode: m.analyserCode, testId: m.testId })));
  }

  const save = useMutation({
    mutationFn: () =>
      put(`/lab/instruments/${instrument.id}/test-maps`, {
        maps: (rows ?? []).filter((r) => r.analyserCode.trim() && r.testId).map((r) => ({ analyserCode: r.analyserCode.trim().toUpperCase(), testId: r.testId })),
      }),
    onSuccess: () => {
      toast.success(tr('lab', 'mapping_saved'));
      void queryClient.invalidateQueries({ queryKey: ['lab-instrument-maps', instrument.id] });
      void queryClient.invalidateQueries({ queryKey: ['lab-instruments'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const current = rows ?? [];

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title={tr('lab', 'x_test_codes', { name: instrument.name })}
      description={tr('lab', 'what_the_analyser_calls_a_test_what_your')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {manage ? tr('lab', 'cancel') : tr('lab', 'close')}
          </Button>
          {manage && (
            <Button
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              loading={save.isPending}
            >
              {tr('lab', 'save_mapping')}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        {error && <Alert variant="destructive">{error}</Alert>}
        <div className="rounded-lg border">
          <div className="grid grid-cols-12 gap-2 border-b bg-muted/50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span className="col-span-4">{tr('lab', 'analyser_sends')}</span>
            <span className="col-span-7">{tr('lab', 'catalogue_test')}</span>
          </div>
          <div className="space-y-2 p-3">
            {current.length === 0 && <p className="text-sm text-muted-foreground">{tr('lab', 'no_codes_mapped')}</p>}
            {current.map((row, index) => (
              <div key={index} className="grid grid-cols-12 items-center gap-2">
                <Input
                  className="col-span-4 h-8 font-mono text-xs"
                  placeholder={tr('lab', 'hgb')}
                  value={row.analyserCode}
                  disabled={!manage}
                  onChange={(e) => setRows(current.map((r, i) => (i === index ? { ...r, analyserCode: e.target.value.toUpperCase() } : r)))}
                />
                <Select
                  className="col-span-7 h-8 text-xs"
                  value={row.testId}
                  disabled={!manage}
                  onChange={(e) => setRows(current.map((r, i) => (i === index ? { ...r, testId: e.target.value } : r)))}
                >
                  <option value="">—</option>
                  {tests.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.code})
                    </option>
                  ))}
                </Select>
                {manage && (
                  <Button variant="ghost" size="icon" className="col-span-1 h-8 w-8" aria-label={tr('lab', 'remove')} onClick={() => setRows(current.filter((_, i) => i !== index))}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
            {manage && (
              <Button size="sm" variant="outline" onClick={() => setRows([...current, { analyserCode: '', testId: '' }])}>
                <Plus className="me-1 h-3.5 w-3.5" /> {tr('lab', 'add_code')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function MessagesDialog({ instrument, onClose }: { instrument: Instrument; onClose: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const messages = useQuery({
    queryKey: ['lab-instrument-messages', instrument.id],
    queryFn: async () => (await get<Paged<Message>>(`/lab/instruments/${instrument.id}/messages?limit=100`)).data,
    refetchInterval: visibleInterval(60_000),
  });

  return (
    <Dialog open onClose={onClose} wide title={tr('lab', 'x_recent_frames', { name: instrument.name })} description={tr('lab', 'every_frame_received_newest_first')}>
      <DataTable
        rows={messages.data}
        loading={messages.isLoading} refreshing={messages.isFetching}
        error={messages.error}
        rowKey={(row) => row.id}
        dense
        empty={tr('lab', 'nothing_received_from_this_analyser_yet')}
        onRowClick={(row) => setOpen(open === row.id ? null : row.id)}
        columns={[
          { header: tr('lab', 'received'), cell: (row) => <span className="text-xs">{dateTime(row.receivedAt)}</span> },
          { header: tr('lab', 'accession'), cell: (row) => <span className="font-mono text-xs">{row.accessionNo ?? '—'}</span> },
          {
            header: tr('lab', 'outcome'),
            cell: (row) => (
              <div>
                <StatusBadge
                  status={row.status}
                  tone={row.status === 'accepted' ? 'success' : row.status === 'partial' ? 'warning' : row.status === 'received' ? 'muted' : 'destructive'}
                />
                <span className="ms-2 text-xs text-muted-foreground">
                  {row.accepted} {tr('lab', 'in')}{row.rejected.length ? tr('lab', 'x_rejected', { length: row.rejected.length }) : ''}
                </span>
                {row.error && <p className="text-xs text-destructive">{row.error}</p>}
                {row.rejected.length > 0 && (
                  <p className="text-xs text-muted-foreground">{row.rejected.map((r) => `${r.testCode}: ${r.reason}`).join(' · ')}</p>
                )}
                {open === row.id && <pre className="mt-2 max-h-48 overflow-auto rounded bg-muted p-2 font-mono text-[11px]">{row.raw}</pre>}
              </div>
            ),
          },
          { header: tr('lab', 'format'), cell: (row) => <span className="text-xs uppercase">{row.format}</span> },
        ]}
      />
    </Dialog>
  );
}
